"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import type { ToolComponentData } from "@/lib/tool-adapter"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { BreakdownMomentCTA } from "@/components/warranty/breakdown-moment-cta"
import type { ApprovedWarrantyCta } from "@/lib/warranty/model"

// Sources:
//   • Manufacturer flat-rate labor codes (Whirlpool, GE, Samsung service docs)
//     for the most common refrigerator repairs.
//   • Consumer Reports member-survey averages for major-appliance service
//     calls: typical residential refrigerator repair $200–$400 incl. parts +
//     labor + diagnostic. Severe component failures (compressor, control
//     board) push higher — survey ranges cited as guidance only.
//   • Industry-typical service-call diagnostic fee: ~$75, frequently credited
//     toward the repair if the customer authorizes work.

const FRIDGE_TYPES = [
  "Top Freezer",
  "Bottom Freezer",
  "Side-by-Side",
  "French Door",
  "Mini Fridge",
  "Built-In",
  "Commercial",
] as const

type FridgeType = (typeof FRIDGE_TYPES)[number]

const REPAIR_COMPONENTS: Record<string, { min: number; max: number; description: string }> = {
  Compressor: { min: 300, max: 600, description: "Heart of the cooling system — most expensive repair" },
  "Ice Maker": { min: 150, max: 350, description: "Ice production mechanism and water inlet valve" },
  Thermostat: { min: 100, max: 250, description: "Temperature control sensor and switch" },
  "Door Seal (Gasket)": { min: 50, max: 150, description: "Rubber seal around door edges" },
  "Evaporator Fan": { min: 150, max: 300, description: "Circulates cold air inside the refrigerator" },
  "Condenser Fan": { min: 100, max: 250, description: "Removes heat from condenser coils" },
  "Control Board": { min: 200, max: 500, description: "Electronic control board — manages all functions" },
  "Water Line": { min: 100, max: 250, description: "Supply line for water dispenser and ice maker" },
}

const TYPE_MULTIPLIERS: Record<FridgeType, number> = {
  "Top Freezer": 0.85,
  "Bottom Freezer": 1.0,
  "Side-by-Side": 1.1,
  "French Door": 1.2,
  "Mini Fridge": 0.7,
  "Built-In": 1.4,
  Commercial: 1.6,
}

const FALLBACK_RELATED = [
  "refrigerator-repair-vs-replace-calculator",
  "refrigerator-lifespan-calculator",
  "refrigerator-cost-of-ownership-calculator",
  "extended-warranty-break-even-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  const candidates = [...slugs, ...FALLBACK_RELATED]
  for (const s of candidates) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function RefrigeratorRepairCostEstimatorClient({
  toolData,
  approvedWarrantyCtas = [],
}: {
  toolData?: ToolComponentData
  /** FRIDGE-1042 — approved warranty programs from the registry; [] renders no slot. */
  approvedWarrantyCtas?: ApprovedWarrantyCta[]
}) {
  const meta = toolData ?? TOOL_METADATA["refrigerator-repair-cost-estimator"]

  // Hooks first, THEN the early return (FRIDGE-307). The `meta` early return
  // used to sit above these three hooks: React requires every hook to be called
  // in the same order on every render, and a component that returns early on
  // one render and reaches the hooks on the next crashes with "Rendered more
  // hooks than during the previous render". None of these hooks reads `meta`,
  // so moving them up changes nothing about what renders.
  const [fridgeType, setFridgeType] = useState<FridgeType>("French Door")
  const [component, setComponent] = useState<string>("Ice Maker")

  const result = useMemo(() => {
    const repair = REPAIR_COMPONENTS[component]
    const multiplier = TYPE_MULTIPLIERS[fridgeType] ?? 1.0
    if (repair === undefined) return null
    const adjustedMin = Math.round(repair.min * multiplier)
    const adjustedMax = Math.round(repair.max * multiplier)
    const avgCost = Math.round((adjustedMin + adjustedMax) / 2)
    const serviceFee = 75
    return {
      adjustedMin,
      adjustedMax,
      avgCost,
      serviceFee,
      withDiagnosticMin: adjustedMin + serviceFee,
      withDiagnosticMax: adjustedMax + serviceFee,
      componentNote: repair.description,
    }
  }, [fridgeType, component])

  if (meta === undefined) return null

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="What does the repair"
      heroTitleItalic="really"
      heroTitlePost="cost?"
      heroSubtitle="Pick your fridge type and the failed component. We combine manufacturer flat-rate labor with published service-call survey ranges to estimate parts + labor + the diagnostic fee."
      heroMeta={["⏱ ~30 sec", "Manufacturer service docs + service-call survey data"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "$200–400", label: "Typical repair total", note: "Owner service-call surveys" },
            { value: "~$75", label: "Diagnostic fee", note: "Often credited" },
            { value: "1.6×", label: "Commercial multiplier", note: "Manufacturer labor" },
            { value: "8 parts", label: "Most common failures", note: "Service docs" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "Why does fridge type change the cost?",
          answer:
            "Manufacturer flat-rate labor codes scale with disassembly difficulty. A built-in or commercial refrigerator takes longer to access internal components than a freestanding top-freezer; the parts catalog price for those models also runs higher.",
        },
        {
          question: "Will the diagnostic fee actually get credited toward the repair?",
          answer:
            "Industry-typical: yes, if you authorize the repair. Most independent and brand-authorized technicians waive or credit the trip fee when they perform the work. Confirm in writing before they begin — terms vary by company and region.",
        },
        {
          question: "Is this just one technician's price or a fair market range?",
          answer:
            "It's a planning range, not a quote. We combine documented manufacturer flat-rate labor with published owner-survey ranges for actual residential refrigerator service. Your local pricing will sit inside this band most of the time.",
        },
        {
          question: "When does the repair tip over toward replacing the fridge instead?",
          answer:
            "A common rule of thumb: if the repair quote exceeds 50% of a comparable replacement and the unit is past 8 years old, replacement usually wins on lifetime cost. Use our repair-vs-replace calculator for the full math.",
        },
        {
          question: "Why is compressor failure so much more expensive than the others?",
          answer:
            "The compressor is sealed inside the refrigeration loop. Replacing it requires evacuating the refrigerant, brazing in the new component, recharging the system, and verifying the leak-check — that's why both the part and the labor codes are the highest in the catalog.",
        },
      ]}
      relatedTools={buildRelated((meta as ToolComponentData)?.relatedTools ?? [], "refrigerator-repair-cost-estimator")}
      cta={{
        title: "Repair quote higher than expected?",
        body: "Run the repair-vs-replace calculator to see whether the repair pays back over the unit's remaining service life or whether a new bottom-freezer makes more sense.",
        buttonLabel: "Run repair vs replace",
        buttonHref: "/tools/refrigerator-repair-vs-replace-calculator",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Pick the type.
                <br />
                Pick the part.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Cost band scales with the manufacturer&apos;s labor code for that fridge type.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Refrigerator type
                  </label>
                  <select
                    value={fridgeType}
                    onChange={(e) => setFridgeType(e.target.value as FridgeType)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    {FRIDGE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Failed component
                  </label>
                  <select
                    value={component}
                    onChange={(e) => setComponent(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    {Object.keys(REPAIR_COMPONENTS).map((c) => (
                      <option key={c} value={c}>
                        {c} ({`$${REPAIR_COMPONENTS[c].min}`}–{`$${REPAIR_COMPONENTS[c].max}`})
                      </option>
                    ))}
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Base ranges are pre-multiplier
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you change selections
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Estimated repair range
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(40px, 6vw, 72px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.95,
                    }}
                  >
                    {`$${result?.adjustedMin}`}–{`$${result?.adjustedMax}`}
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.componentNote}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Average
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.avgCost}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        With ~$75 diagnostic
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.withDiagnosticMin}`}–{`$${result?.withDiagnosticMax}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BREAKDOWN-MOMENT SLOT (FRIDGE-1042) — nothing until approved ============ */}
      <BreakdownMomentCTA programs={approvedWarrantyCtas} context="a repair estimate in hand" />

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · If repair tips toward replace"
          title="Bottom-freezer refrigerators"
          description="Strong reliability records and accessible service points if anything fails down the line."
          collectionType="bottom-freezer"
          fallbackCollectionType="top-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Manufacturer labor codes × <em className="italic text-[#ff9951]">survey</em> ranges.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Component cost bands come from documented manufacturer flat-rate labor. Type
                multipliers reflect the disassembly time those service docs allot to each design.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Base range per component",
                  d: "Each of eight common failures has a parts + labor band documented in the major-brand service catalogs. Compressor and control board run highest; door seals lowest.",
                },
                {
                  n: "02",
                  t: "Type multiplier",
                  d: "Mini ×0.7, top-freezer ×0.85, bottom-freezer ×1.0, side-by-side ×1.1, French door ×1.2, built-in ×1.4, commercial ×1.6 — derived from manufacturer flat-rate labor differentials.",
                },
                {
                  n: "03",
                  t: "Diagnostic fee",
                  d: "We add a US-typical $75 service-call fee to the with-diagnostic line. Most technicians credit it toward the repair when authorized — confirm before work begins.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer refrigerators with the lowest repair rates"
          description="Simpler designs with fewer failure points and more accessible service locations."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
